import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Edit,
  Trash2,
  Users,
  Building2,
  Mail,
  CreditCard,
  Calendar,
  Globe,
  Settings,
  BarChart3,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  ExternalLink,
  Shield,
  HardDrive,
  FileEdit,
  Briefcase,
  Send
} from 'lucide-react'
import { INDUSTRIES } from '../../utils/industrySchemas'
import LoadingSpinner from '../common/LoadingSpinner'
import AdminNotes from './AdminNotes'
import ClientPermissions from './ClientPermissions'

const ClientDetail = () => {
  const navigate = useNavigate()
  const { clientId } = useParams()
  const queryClient = useQueryClient()

  const { data: client, isLoading, error } = useQuery(
    ['client', clientId],
    () => apiService.get(`/clients/profile?clientId=${clientId}`).then(res => res.data),
    { enabled: !!clientId, retry: 1 }
  )

  const { data: plansRaw } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(r => r.data),
    { staleTime: 5 * 60 * 1000 }
  )

  const { data: billingData } = useQuery(
    ['client-billing', clientId],
    () => apiService.get(`/billing/client/${clientId}`).then(res => res.data),
    { enabled: !!clientId, retry: 1 }
  )

  const { data: clientDomains } = useQuery(
    ['client-domains', clientId],
    () => apiService.get('/domains').then(res => {
      const domains = res.data?.domains || []
      return domains.filter(d => d.client_id === clientId)
    }),
    { enabled: !!clientId }
  )

  const resendInvitationMutation = useMutation(
    () => apiService.post(`/clients/${clientId}/resend-invitation`),
    {
      onSuccess: () => toast.success(`Invitation email resent to ${client?.email}`),
      onError: (error) => toast.error(error.response?.data?.error || 'Failed to resend invitation')
    }
  )

  const deleteClientMutation = useMutation(
    () => apiService.delete(`/clients/${clientId}`),
    {
      onSuccess: () => {
        toast.success('Client deleted successfully')
        navigate('/admin/clients')
      },
      onError: (error) => toast.error(error.response?.data?.error || 'Failed to delete client')
    }
  )

  const handleDeleteClient = () => {
    if (confirm(`Are you sure you want to delete ${client?.company_name}? This action cannot be undone and will delete all associated sites and data.`)) {
      deleteClientMutation.mutate()
    }
  }

  const formatCurrency = (amount) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount)

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-GB', {
    year: 'numeric', month: 'long', day: 'numeric'
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 256 }}>
        <LoadingSpinner size="lg" text="Loading client details..." />
      </div>
    )
  }

  if (error || !client) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <AlertCircle style={{ width: 64, height: 64, color: 'var(--danger)', margin: '0 auto 16px' }} />
        <h3 className="font-medium" style={{ marginBottom: 8 }}>Client Not Found</h3>
        <p className="text-muted" style={{ marginBottom: 24, fontSize: 13 }}>The client you're looking for doesn't exist or has been deleted.</p>
        <button className="btn btn-primary" onClick={() => navigate('/admin/clients')}>Back to Clients</button>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-4">
          <button className="btn btn-ghost btn-icon" onClick={() => navigate('/admin/clients')}>
            <ArrowLeft style={{ width: 20, height: 20 }} />
          </button>
          <div>
            <h1 className="h2">{client.company_name}</h1>
            <p className="text-muted" style={{ fontSize: 13 }}>Client Details &amp; Management</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button className="btn" onClick={() => resendInvitationMutation.mutate()} disabled={resendInvitationMutation.isLoading}>
            {resendInvitationMutation.isLoading ? <LoadingSpinner size="sm" /> : (
              <><Send style={{ width: 16, height: 16, marginRight: 8 }} />Resend Invitation</>
            )}
          </button>
          <button className="btn" onClick={() => navigate(`/admin/clients/${clientId}/edit`)}>
            <Edit style={{ width: 16, height: 16, marginRight: 8 }} />Edit Client
          </button>
          <button className="btn btn-danger" onClick={handleDeleteClient} disabled={deleteClientMutation.isLoading}>
            {deleteClientMutation.isLoading ? <LoadingSpinner size="sm" /> : (
              <><Trash2 style={{ width: 16, height: 16, marginRight: 8 }} />Delete</>
            )}
          </button>
        </div>
      </motion.div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24 }}>
        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Basic Info */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
            <div className="card card-pad">
              <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
                <div style={{ padding: 8, background: 'var(--accent-soft)', borderRadius: 'var(--radius)' }}>
                  <Users style={{ width: 20, height: 20, color: 'var(--accent)' }} />
                </div>
                <span className="font-semibold">Client Information</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label className="text-muted font-medium" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Contact Name</label>
                  <p style={{ fontSize: 13 }}>{client.user_name}</p>
                </div>
                <div>
                  <label className="text-muted font-medium" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Email Address</label>
                  <p style={{ fontSize: 13 }}>{client.email}</p>
                </div>
                <div>
                  <label className="text-muted font-medium" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Company Name</label>
                  <p style={{ fontSize: 13 }}>{client.company_name}</p>
                </div>
                <div>
                  <label className="text-muted font-medium" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Industry</label>
                  <span className="badge badge-accent">
                    {INDUSTRIES[client.industry]?.icon || '🏢'} {INDUSTRIES[client.industry]?.label || 'General Business'}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Custom Domains */}
          <CustomDomainsSection clientDomains={clientDomains} clientId={clientId} navigate={navigate} />

          {/* Admin Notes */}
          <AdminNotes clientId={clientId} />

          {/* Client Permissions */}
          <ClientPermissions clientId={clientId} />

          {/* Subscription */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
            <div className="card card-pad">
              <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
                <div style={{ padding: 8, background: 'var(--accent-soft)', borderRadius: 'var(--radius)' }}>
                  <CreditCard style={{ width: 20, height: 20, color: 'var(--accent)' }} />
                </div>
                <span className="font-semibold">Subscription &amp; Billing</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label className="text-muted font-medium" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Current Plan</label>
                  <span className="badge badge-accent">
                    {(plansRaw?.[client.subscription_plan]?.name || client.subscription_plan)?.toUpperCase() || 'BASIC'}
                  </span>
                </div>
                <div>
                  <label className="text-muted font-medium" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Monthly Cost</label>
                  <p style={{ fontSize: 13 }}>{billingData?.plan ? formatCurrency(billingData.plan.price) : 'Loading...'}</p>
                </div>
                <div>
                  <label className="text-muted font-medium" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Edit Quota</label>
                  <p style={{ fontSize: 13 }}>{client.edits_used || 0} / {client.edit_quota || client.monthly_edit_limit || 50} edits</p>
                  <div style={{ width: '100%', height: 6, background: 'var(--surface-2)', borderRadius: 999, marginTop: 6 }}>
                    <div style={{
                      height: 6, borderRadius: 999, background: 'var(--accent)',
                      width: `${Math.min(((client.edits_used || 0) / (client.edit_quota || client.monthly_edit_limit || 50)) * 100, 100)}%`
                    }} />
                  </div>
                </div>
                <div>
                  <label className="text-muted font-medium" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Next Billing Date</label>
                  <p style={{ fontSize: 13 }}>{billingData?.nextBillingDate ? formatDate(billingData.nextBillingDate) : 'N/A'}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Usage Statistics */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}>
            <div className="card card-pad">
              <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
                <div style={{ padding: 8, background: 'var(--success-soft)', borderRadius: 'var(--radius)' }}>
                  <BarChart3 style={{ width: 20, height: 20, color: 'var(--success)' }} />
                </div>
                <span className="font-semibold">Usage Statistics</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24 }}>
                {[
                  { icon: <Globe style={{ width: 16, height: 16 }} />, value: client.site_count || 0, label: 'Total Sites' },
                  { icon: <CheckCircle style={{ width: 16, height: 16, color: 'var(--success)' }} />, value: client.published_sites || 0, label: 'Published Sites' },
                  { icon: <FileEdit style={{ width: 16, height: 16, color: 'var(--accent)' }} />, value: client.edits_used || 0, label: 'Edits This Month' },
                  { icon: <HardDrive style={{ width: 16, height: 16 }} />, value: client.storage_used_mb ? `${client.storage_used_mb} MB` : '0 MB', label: 'Storage Used', sub: client.file_count > 0 ? `${client.file_count} file${client.file_count !== 1 ? 's' : ''}` : null },
                ].map((stat, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <div className="flex items-center justify-center" style={{ marginBottom: 4, color: 'var(--text-muted)' }}>{stat.icon}</div>
                    <p className="font-bold" style={{ fontSize: 24, margin: '0 0 2px' }}>{stat.value}</p>
                    <p className="text-muted" style={{ fontSize: 12 }}>{stat.label}</p>
                    {stat.sub && <p className="text-faint" style={{ fontSize: 11, marginTop: 2 }}>{stat.sub}</p>}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Quick Actions */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <div className="card card-pad">
              <p className="font-semibold" style={{ marginBottom: 12 }}>Quick Actions</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button className="btn btn-primary w-full" onClick={() => navigate('/admin/sites/new', { state: { clientId: client.id } })}>
                  <Globe style={{ width: 16, height: 16, marginRight: 8 }} />Create New Site
                </button>
                <button className="btn w-full" onClick={() => navigate('/admin/clients', { state: { filter: client.subscription_plan } })}>
                  <Users style={{ width: 16, height: 16, marginRight: 8 }} />View Similar Clients
                </button>
                <button className="btn w-full" onClick={() => navigate('/admin/billing', { state: { clientId: client.id } })}>
                  <CreditCard style={{ width: 16, height: 16, marginRight: 8 }} />View Billing Details
                </button>
              </div>
            </div>
          </motion.div>

          {/* Account Status */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
            <div className="card card-pad">
              <p className="font-semibold" style={{ marginBottom: 12 }}>Account Status</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className="flex justify-between items-center">
                  <span className="text-soft" style={{ fontSize: 13 }}>Account:</span>
                  <span className={client.is_active ? 'badge badge-success' : 'badge badge-danger'}>
                    {client.is_active ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-soft" style={{ fontSize: 13 }}>Billing:</span>
                  {(() => {
                    const isFreePlan = plansRaw?.[client.subscription_plan]?.price === 0
                    const s = isFreePlan ? 'active' : client.billing_status
                    const badgeMap = { active: 'badge-success', trialing: 'badge-success', past_due: 'badge-danger', unpaid: 'badge-danger' }
                    const labels = { active: 'Active', trialing: 'Trialing', past_due: 'Past due', unpaid: 'Unpaid' }
                    return (
                      <span className={`badge ${badgeMap[s] || 'badge-warning'}`}>
                        {labels[s] || 'Pending payment'}
                      </span>
                    )
                  })()}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-soft" style={{ fontSize: 13 }}>Member Since:</span>
                  <span style={{ fontSize: 13 }}>{client.created_at ? formatDate(client.created_at) : 'N/A'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-soft" style={{ fontSize: 13 }}>Last Login:</span>
                  <span style={{ fontSize: 13 }}>{client.last_login ? formatDate(client.last_login) : 'Never'}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

const CustomDomainsSection = ({ clientDomains, clientId, navigate }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified': case 'active': return <CheckCircle style={{ width: 14, height: 14, color: 'var(--success)' }} />
      case 'pending': return <Clock style={{ width: 14, height: 14, color: 'var(--warning)' }} />
      case 'dns_configured': return <AlertCircle style={{ width: 14, height: 14, color: 'var(--accent)' }} />
      case 'failed': return <XCircle style={{ width: 14, height: 14, color: 'var(--danger)' }} />
      default: return <Clock style={{ width: 14, height: 14, color: 'var(--text-muted)' }} />
    }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified': case 'active': return 'badge-success'
      case 'pending': return 'badge-warning'
      case 'dns_configured': return 'badge-accent'
      case 'failed': return 'badge-danger'
      default: return ''
    }
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
      <div className="card card-pad">
        <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
          <div className="flex items-center gap-3">
            <div style={{ padding: 8, background: 'var(--accent-soft)', borderRadius: 'var(--radius)' }}>
              <Globe style={{ width: 20, height: 20, color: 'var(--accent)' }} />
            </div>
            <span className="font-semibold">Custom Domains</span>
          </div>
          <span className="text-muted" style={{ fontSize: 13 }}>
            {clientDomains?.length || 0} domain{clientDomains?.length !== 1 ? 's' : ''}
          </span>
        </div>

        {clientDomains && clientDomains.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {clientDomains.map((domain) => {
              const fullDomain = domain.subdomain ? `${domain.subdomain}.${domain.domain}` : domain.domain
              return (
                <div key={domain.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 12, border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--surface)' }}>
                  <div className="flex items-center gap-3">
                    <Globe style={{ width: 18, height: 18, color: 'var(--text-muted)' }} />
                    <div>
                      <p className="font-medium" style={{ fontSize: 13 }}>{fullDomain}</p>
                      <div className="flex items-center gap-2" style={{ marginTop: 4 }}>
                        <span className={`badge ${getStatusBadge(domain.status)}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {getStatusIcon(domain.status)}
                          <span style={{ textTransform: 'capitalize' }}>{domain.status.replace('_', ' ')}</span>
                        </span>
                        {!!domain.ssl_enabled && (
                          <span className="badge badge-success" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Shield style={{ width: 10, height: 10 }} />SSL
                          </span>
                        )}
                      </div>
                      {domain.verified_at && (
                        <p className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>
                          Verified on {new Date(domain.verified_at).toLocaleDateString()}
                        </p>
                      )}
                      {domain.error_message && (
                        <p style={{ fontSize: 11, color: 'var(--danger)', marginTop: 2 }}>{domain.error_message}</p>
                      )}
                    </div>
                  </div>
                  {domain.status === 'verified' && domain.azure_published_url && (
                    <a href={domain.azure_published_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }} title="View Live Site">
                      <ExternalLink style={{ width: 16, height: 16 }} />
                    </a>
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Globe style={{ width: 48, height: 48, color: 'var(--text-faint)', margin: '0 auto 12px' }} />
            <p className="font-medium" style={{ fontSize: 13, marginBottom: 4 }}>No custom domains</p>
            <p className="text-muted" style={{ fontSize: 12, marginBottom: 12 }}>This client hasn't set up any custom domains yet.</p>
            <button className="btn btn-sm" onClick={() => navigate(`/admin/clients/${clientId}/edit`)}>
              Add Domain
            </button>
          </div>
        )}
      </div>
    </motion.div>
  )
}

export default ClientDetail
